import { useEffect, useState } from "react";
import { Loader2, NotebookPen, Pencil, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Btn, IconBtn } from "../../ui";

/**
 * The note attached to a finished call.
 *
 * Unlike the live window's box - which autosaves, because the moment a call
 * ends is not the typist's to choose - this one is an explicit edit with a save
 * button. Reading a call log is a browsing activity, and a field that writes
 * itself while somebody is scrolling past it is a field that eats notes by
 * accident.
 */
export function CallNote({
  note,
  editing,
  onEditing,
  onSave,
}: {
  note: string | null;
  editing: boolean;
  onEditing: (v: boolean) => void;
  onSave: (value: string | null) => Promise<void>;
}) {
  const t = useT();
  const [draft, setDraft] = useState(note ?? "");
  const [busy, setBusy] = useState(false);

  // Opening the editor, or switching to another call, starts from what is
  // actually stored rather than from the last thing that was typed anywhere.
  useEffect(() => {
    if (editing) setDraft(note ?? "");
  }, [editing, note]);

  async function commit(value: string | null) {
    setBusy(true);
    try {
      await onSave(value);
      onEditing(false);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    if (!note) return null;
    return (
      <div className="crm-note">
        <div className="crm-note-t">
          <NotebookPen className="size-4" />
          {t("calls.detail.note")}
          <span className="crm-push">
            <IconBtn small onClick={() => onEditing(true)} title={t("calls.detail.editNote")}>
              <Pencil className="size-3.5" />
            </IconBtn>
          </span>
        </div>
        <p className="crm-note-p">{note}</p>
      </div>
    );
  }

  return (
    <div className="crm-note">
      <div className="crm-note-t">
        <NotebookPen className="size-4" />
        {t("calls.detail.note")}
      </div>
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t("calls.detail.notePlaceholder")}
        className="crm-note-ta"
      />
      <div className="mt-2 flex items-center gap-2">
        <Btn small tone="dark" onClick={() => void commit(draft.trim() ? draft : null)} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {t("common.save")}
        </Btn>
        <Btn small onClick={() => onEditing(false)} disabled={busy}>
          {t("common.cancel")}
        </Btn>
        {note ? (
          <span className="crm-push">
            <IconBtn small onClick={() => void commit(null)} disabled={busy} title={t("calls.detail.removeNote")}>
              <Trash2 className="size-3.5" />
            </IconBtn>
          </span>
        ) : null}
      </div>
    </div>
  );
}
